import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { BUSINESS, SERVICES } from '../data/content';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/coverage', label: 'Coverage' },
  { to: '/reviews', label: 'Reviews' },
  { to: '/blog', label: 'Blog' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  return (
    <header data-testid="site-navbar" className={`sticky top-0 z-40 transition-all ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
            <div className="flex items-baseline gap-0.5">
              <span className="font-outfit text-2xl md:text-[26px] font-light tracking-[0.08em] text-forest leading-none">LARA</span>
              <span className="font-outfit text-2xl md:text-[26px] font-light tracking-[0.08em] text-forest leading-none">É</span>
            </div>
            <div className="hidden sm:block w-px h-7 bg-oat mx-1.5" />
            <span className="hidden sm:inline font-manrope text-[10px] uppercase tracking-[0.32em] text-ink/55 leading-none mt-1">Logistics</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map(item => (
              <NavLink key={item.to} to={item.to} end={item.to === '/'} data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={({ isActive }) => `font-manrope text-sm font-medium transition-colors ${isActive ? 'text-forest' : 'text-ink/70 hover:text-forest'}`}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="navbar-call-btn" className="flex items-center gap-2 text-sm font-medium text-ink hover:text-forest transition-colors">
              <Phone size={16} /> {BUSINESS.phone}
            </a>
            <Link to="/quote" data-testid="navbar-quote-btn" className="bg-forest hover:bg-forest/90 text-white text-sm font-medium rounded-full px-5 py-2.5 transition-colors">
              Get Free Quote
            </Link>
          </div>

          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden text-ink p-2" aria-label="Open menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-sand">
          <div className="px-5 py-4 flex flex-col gap-1">
            {NAV.map(item => (
              <Link key={item.to} to={item.to} data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className="py-3 font-manrope text-base text-ink border-b border-sand last:border-none">{item.label}</Link>
            ))}
            <div className="grid grid-cols-2 gap-3 mt-4">
              <a href={`tel:${BUSINESS.phoneRaw}`} className="bg-sand text-ink text-sm font-medium rounded-full px-4 py-3 text-center">Call us</a>
              <Link to="/quote" className="bg-forest text-white text-sm font-medium rounded-full px-4 py-3 text-center">Free Quote</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
